import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Lock, Check, AlertCircle } from 'lucide-react';
import api from '../lib/api';

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/customer/reset-password/${token}`, { password });
      if (res.data?.success) {
        setDone(true);
        setTimeout(() => navigate('/', { replace: true }), 2500);
      } else {
        setError(res.data?.message || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Reset link expired or invalid');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-green-200 p-6">
        <div className="bg-white border-4 border-black shadow-[12px_12px_0px_black] max-w-md w-full p-10 text-center">
          <div className="inline-block p-3 bg-black text-white rounded-full mb-4 animate-bounce">
            <Check size={40} strokeWidth={4} />
          </div>
          <h1 className="text-3xl font-black tracking-tighter mb-2">PASSWORD UPDATED</h1>
          <p className="text-sm font-mono opacity-70">Redirecting to home…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-yellow-300 p-6">
      <form
        onSubmit={handleSubmit}
        className="bg-white border-4 border-black shadow-[12px_12px_0px_black] max-w-md w-full p-8"
      >
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter mb-2 flex items-center gap-3">
          <Lock size={28} /> RESET PASSWORD
        </h1>
        <p className="text-sm font-mono text-gray-600 mb-6">
          Choose a new password. At least 6 characters.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border-2 border-red-600 inline-flex items-start gap-2">
            <AlertCircle size={18} className="text-red-600 flex-shrink-0" />
            <div className="text-sm font-bold text-red-800">{error}</div>
          </div>
        )}

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-xs font-black uppercase tracking-widest mb-1">New password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full p-3 border-2 border-black font-mono"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-xs font-black uppercase tracking-widest mb-1">Confirm new password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              className="w-full p-3 border-2 border-black font-mono"
              autoComplete="new-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !password || !confirm}
          className="w-full bg-black text-white py-3 font-black uppercase border-4 border-yellow-400 hover:bg-yellow-400 hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? 'UPDATING…' : 'SET NEW PASSWORD'}
        </button>

        <div className="mt-4 text-center text-xs">
          <Link to="/" className="font-bold underline">← Back home</Link>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordPage;
